import React from 'react';
import useBrokerageConnections from 'Components/Banking/useBrokerageConnections';
import BrokerageLoginForm from 'Components/Banking/BrokerageLoginForm';
import useEscapeToClose from 'Components/Banking/useEscapeToClose';
import applyBrokerageSession from 'Components/Banking/applyBrokerageSession';
import './Brokerage.css';

// Eingebettetes Limit-Order-Formular (Order pruefen -> ggf. eigene TAN ->
// platziert). Wird sowohl vom Orderbuch als auch von den Ordervorlagen,
// der Depotuebersicht (Kaufen/Verkaufen-Icons) und dem Watchlist-Kauf
// verwendet - initialOrder liefert die Vorbelegung. instrumentLabel ist
// optional (nur wenn der Aufrufer die Wertpapierbezeichnung schon kennt).
//
// Depot/Verbindung (Umbau 2026-07-26, Nutzerwunsch): ist props.connectionId
// gesetzt (Verkauf einer bekannten Position, Ordervorlage - der Aufrufer
// kennt Verbindung+Depot bereits), wird das Feld gesperrt angezeigt. Ist
// props.connectionId NICHT gesetzt (Kauf aus der Watchlist), waehlt das
// Formular selbst Verbindung und Depot aus dem lokalen Spiegel - keine
// separaten Auswahl-Bildschirme mehr davor.
//
// Handelsplaetze kommen nach Moeglichkeit aus dem beim letzten Depot-Sync
// gecachten Bestand (BrokerageLocalOrderDimensions, kein comdirect-Zugriff
// noetig) - fuer eine bekannte, bereits gehaltene Position ist das Formular
// damit komplett offline befuellbar, die Verbindung wird erst beim Klick auf
// "Order absenden" geoeffnet (BrokerageOrderPreview liefert dann
// 'login-required', hier wird inline der PIN/TAN-Dialog eingeblendet und die
// Pruefung danach automatisch wiederholt).
//
// Ist der Cache fuer dieses Instrument leer (z.B. neu aus der Watchlist, WKN
// wurde noch nie synchronisiert), waere eine leere Handelsplatz-Auswahl
// nutzlos - in dem Fall wird die Verbindung stattdessen SOFORT geoeffnet, nur
// um die Handelsplaetze live nachzuladen (BrokerageOrderDimensions). Die
// Session bleibt danach offen (purpose=order schliesst nicht sofort wieder,
// siehe BrokerageTanConfirm.inc), sodass beim spaeteren "Order absenden" kein
// zweiter TAN-Dialog mehr noetig ist.
export default function BrokerageOrderForm(props) {

	const x = props.x;
	const task = (name, data, cb) => x.was.axPost('Backend/webapp.php?module=Banking&task=' + name, data, cb);

	const depotLocked = !!props.connectionId;

	const [order, setOrder] = React.useState(props.initialOrder);
	const [venues, setVenues] = React.useState(null);
	const [needsLogin, setNeedsLogin] = React.useState(false);
	// 'venues' (Cache leer, Verbindung dient nur dem Nachladen der
	// Handelsplaetze) oder 'submit' (Order absenden ohne aktive Session).
	const [loginReason, setLoginReason] = React.useState('venues');
	const [challenge, setChallenge] = React.useState(null);
	const [tanCode, setTanCode] = React.useState('');
	const [busy, setBusy] = React.useState(false);
	const [error, setError] = React.useState('');
	// Direkt nach dem Login-Dialog (loginReason='submit') wird submitPreview()
	// automatisch nachgeschickt (siehe onLoggedIn unten) - ohne diesen Zustand
	// wuerde fuer den kurzen Moment bis zur Serverantwort wieder die volle,
	// bereits abgesendete Ordermaske aufblitzen, bevor der Dialog schliesst
	// (Chat vom 2026-07-26, Nutzer-Bugreport).
	const [autoSubmitting, setAutoSubmitting] = React.useState(false);

	// Nur im ungesperrten Fall (Watchlist-Kauf) ueberhaupt gebraucht.
	const allConnections = useBrokerageConnections(depotLocked ? null : x);
	const [selConnectionId, setSelConnectionId] = React.useState(null);
	const [depots, setDepots] = React.useState(null);

	const connectionId = depotLocked ? props.connectionId : selConnectionId;

	// Schliesst nur noch den Dialog, widerruft die comdirect-Verbindung NICHT
	// mehr (Umbau 2026-07-26): die Sitzung bleibt jetzt bewusst bestehen, auch
	// wenn dieser eine Dialog abgebrochen wird, damit sie fuer weitere
	// Aktionen auf der Seite wiederverwendet werden kann. Ein hier verworfenes
	// pendingOrder wird beim naechsten Versuch ohnehin frisch ueberschrieben
	// (BrokerageOrderPreview.inc). Der eigentliche Verbindungsabbau laeuft nur
	// noch ueber den expliziten Logout in der Top-Navigation.
	useEscapeToClose(props.onCancel);

	// Erste Verbindung automatisch vorbelegen, wenn nur eine existiert -
	// trotzdem als (dann einzige) Auswahlmoeglichkeit sichtbar, siehe Rendering
	// unten, damit die Verbindung immer erkennbar bleibt.
	React.useEffect(() => {
		if (!depotLocked && selConnectionId === null && allConnections && allConnections.length > 0) {
			setSelConnectionId(allConnections[0].connectionid);
		}
	}, [depotLocked, allConnections, selConnectionId]);

	React.useEffect(() => {
		if (depotLocked || connectionId === null) return;
		task('BrokerageLocalDepotList', { connectionId }, (result) => {
			const loaded = result.data.returncode === 1 ? (result.data.content.depots || []) : [];
			setDepots(loaded);
			if (loaded.length > 0) {
				setOrder((current) => ({
					...current,
					depotId: loaded[0].depotId,
					settlementAccountId: loaded[0].settlementAccountId,
				}));
			}
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [connectionId, depotLocked]);

	function selectDepot(depotId) {
		const depot = (depots || []).find((d) => d.depotId === depotId);
		setOrder({ ...order, depotId, settlementAccountId: depot ? depot.settlementAccountId : '' });
	}

	function loadLiveVenues() {
		task('BrokerageOrderDimensions', { connectionId, instrumentId: props.initialOrder.instrumentId }, (result) => {
			setVenues(result.data.returncode === 1 ? (result.data.content.venues || []) : []);
		});
	}

	// Wartet im ungesperrten Fall, bis eine Verbindung feststeht (die
	// Handelsplaetze haengen an ihr) - im gesperrten Fall steht sie sofort fest.
	React.useEffect(() => {
		if (connectionId === null) return;
		task('BrokerageLocalOrderDimensions', { connectionId, instrumentId: props.initialOrder.instrumentId }, (result) => {
			const cached = result.data.returncode === 1 ? (result.data.content.venues || []) : [];
			if (cached.length > 0) {
				setVenues(cached);
				return;
			}
			// Kein Cache fuer dieses Instrument - Verbindung sofort oeffnen, um die
			// Handelsplaetze live zu holen, statt eine leere Auswahl zu zeigen.
			setLoginReason('venues');
			setNeedsLogin(true);
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [connectionId]);

	// Heimatboerse (bereits mit Stern markiert, siehe venueLabel()) automatisch
	// vorbelegen, statt eine leere Auswahl zu zeigen - nur wenn noch kein
	// Handelsplatz gewaehlt ist (z.B. nicht bei einem Verkauf mit bereits
	// vorbelegtem venueId).
	React.useEffect(() => {
		if (!venues || venues.length === 0 || order.venueId !== '') return;
		const home = venues.find((v) => isHomeVenue(v));
		if (home) selectVenue(home.venueId);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [venues]);

	// Gleichnamige Handelsplaetze (unterschiedliche venueId, z.B. je nach
	// unterstuetzter Order-Art) mit dem Land ergaenzen, damit sie in der
	// Auswahl unterscheidbar bleiben.
	function venueLabel(venue) {
		const duplicateCount = (venues || []).filter((v) => v.name === venue.name).length;
		const label = duplicateCount > 1 && venue.country ? `${venue.name} (${venue.country})` : venue.name;
		return isHomeVenue(venue) ? `★ ${label}` : label;
	}

	// Bevorzugt das tatsaechlich beobachtete Lagerstellen-Land der Position
	// (custodyType/currentPrice.venue.country aus der Depotuebersicht, siehe
	// BrokerageTask::syncPositions()) - viel zuverlaessiger als die reine
	// ISIN-Heuristik, da comdirect an einem anderen Handelsplatz gekaufte
	// Stuecke oft gar nicht verkaufen laesst ("nicht genuegend Bestand", Chat
	// vom 2026-07-26). Faellt auf die ISIN-Heuristik zurueck, wenn die Position
	// unbekannt ist (z.B. Kauf einer komplett neuen Position).
	function isHomeVenue(venue) {
		const homeCountry = props.custodyCountry || (props.instrumentIsin ? props.instrumentIsin.slice(0, 2) : null);
		return homeCountry !== null && venue.country === homeCountry;
	}

	// Reihenfolge: zuerst Heimatboerse(n) (Heuristik s.o., falls vorhanden),
	// danach Tradegate (falls verfuegbar und nicht schon Heimatboerse), danach
	// der Rest in der vom Backend gelieferten alphabetischen Reihenfolge.
	const sortedVenues = React.useMemo(() => {
		if (!venues) return [];
		const home = venues.filter((v) => isHomeVenue(v));
		const homeIds = new Set(home.map((v) => v.venueId));
		const tradegate = venues.filter((v) => !homeIds.has(v.venueId) && /tradegate/i.test(v.name));
		const tradegateIds = new Set(tradegate.map((v) => v.venueId));
		const rest = venues.filter((v) => !homeIds.has(v.venueId) && !tradegateIds.has(v.venueId));
		return [...home, ...tradegate, ...rest];
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [venues, props.instrumentIsin, props.custodyCountry]);

	// comdirect kennt kein echtes "GTC" (zeitlich unbegrenzt) - live bestaetigt
	// nur "GFD" (Good For Day, tagesgueltig) und "GTD" (Good Till Date, gueltig
	// bis zu einem konkreten Datum, laut comdirect max. 90 Tage). GTD als
	// praktisches Aequivalent zu "so lange wie moeglich" vorbelegt (Chat vom
	// 2026-07-25).
	function maxValidityDate() {
		const date = new Date();
		date.setDate(date.getDate() + 90);
		return date.toISOString().slice(0, 10);
	}

	const selectedConnection = allConnections ? allConnections.find((c) => c.connectionid === connectionId) : null;
	const selectedVenue = (venues || []).find((v) => v.venueId === order.venueId);
	const dayValidity = selectedVenue && (selectedVenue.validityTypes || []).find((t) => t === 'GFD');
	const untilDateValidity = selectedVenue && (selectedVenue.validityTypes || []).find((t) => t === 'GTD');
	const validityOptions = [dayValidity, untilDateValidity].filter(Boolean);

	function selectVenue(venueId) {
		const venue = (venues || []).find((v) => v.venueId === venueId);
		const untilDate = venue && (venue.validityTypes || []).find((t) => t === 'GTD');
		const day = venue && (venue.validityTypes || []).find((t) => t === 'GFD');
		// Abrechnungswaehrung des Handelsplatzes fuer das Limit-Feld - nicht
		// immer EUR (comdirect-Fehler "...ungueltig_fuer_handelspartner" sonst
		// bei z.B. auslaendischen Heimatboersen, Chat vom 2026-07-25).
		const currency = venue && (venue.currencies || [])[0];
		setOrder({
			...order,
			venueId,
			validityType: untilDate || day || '',
			validity: untilDate ? maxValidityDate() : '',
			limitCurrency: currency || 'EUR',
		});
	}

	function orderValid() {
		return order.depotId !== '' && order.instrumentId !== '' && order.venueId !== ''
			&& order.validityType !== '' && (order.validityType !== 'GTD' || order.validity !== '')
			&& order.quantity !== '' && order.limitPrice !== '';
	}

	function submitPreview() {
		setBusy(true);
		setError('');
		task('BrokerageOrderPreview', { connectionId, ...order }, (result) => {
			setBusy(false);
			setAutoSubmitting(false);
			const content = result.data.content || {};
			if (result.data.returncode !== 1) {
				if (content.status === 'login-required') {
					setLoginReason('submit');
					setNeedsLogin(true);
					return;
				}
				setError(content.message || 'Order-Prüfung fehlgeschlagen.');
				return;
			}
			if (content.status === 'tan-required') {
				setChallenge({ type: content.challengeType });
				setTanCode('');
				return;
			}
			applyBrokerageSession(x, content);
			x.wah.addHint('Order ' + content.orderId + ' platziert.');
			props.onDone(content.orderId);
		});
	}

	function submitTan() {
		setBusy(true);
		setError('');
		task('BrokerageOrderConfirm', { connectionId, tanCode }, (result) => {
			setBusy(false);
			const content = result.data.content || {};
			if (result.data.returncode !== 1) {
				setError(content.message || 'Order-Bestätigung fehlgeschlagen.');
				return;
			}
			applyBrokerageSession(x, content);
			x.wah.addHint('Order ' + content.orderId + ' platziert.');
			props.onDone(content.orderId);
		});
	}

	if (needsLogin) {
		return (
			<BrokerageLoginForm
				x={x}
				connectionId={connectionId}
				accountLabel={props.accountLabel || (selectedConnection ? selectedConnection.bankname + ' – ' + selectedConnection.accountlabel : '')}
				pinUse={true}
				pinStored={!!props.pinStored}
				purpose="order"
				onLoggedIn={() => {
					setNeedsLogin(false);
					if (loginReason === 'venues') {
						loadLiveVenues();
					} else {
						setAutoSubmitting(true);
						submitPreview();
					}
				}}
				onCancel={props.onCancel}
			/>
		);
	}

	if (autoSubmitting) {
		return (
			<div className="brokerage-card">
				{props.onCancel && <i className="bi bi-x-lg brokerage-card-close" title="Schließen" onClick={props.onCancel}></i>}
				<p className="brokerage-hint"><i className="bi bi-arrow-repeat"></i> Order wird gesendet…</p>
			</div>
		);
	}

	if (challenge !== null) {
		const needsCode = challenge.type === 'M_TAN';
		return (
			<div className="brokerage-card">
				{props.onCancel && <i className="bi bi-x-lg brokerage-card-close" title="Schließen" onClick={props.onCancel}></i>}
				{challenge.type === 'P_TAN_PUSH' &&
					<p className="brokerage-hint"><i className="bi bi-phone"></i> Bitte in der comdirect-App bestätigen.</p>
				}
				{needsCode &&
					<React.Fragment>
						<label htmlFor="brokerage-order-tan">TAN</label>
						<input
							id="brokerage-order-tan"
							type="text"
							autoComplete="off"
							value={tanCode}
							onChange={(e) => setTanCode(e.target.value)}
							onKeyDown={(e) => { if (e.key === 'Enter') submitTan(); }}
							autoFocus
						/>
					</React.Fragment>
				}
				{error !== '' && <p className="brokerage-error"><i className="bi bi-exclamation-triangle"></i> {error}</p>}
				<button type="button" className="btn" disabled={busy || (needsCode && tanCode === '')} onClick={submitTan}>
					{busy ? 'Bestätige…' : 'Order bestätigen'}
				</button>
			</div>
		);
	}

	return (
		<div className="brokerage-card">
			{props.onCancel && <i className="bi bi-x-lg brokerage-card-close" title="Schließen" onClick={props.onCancel}></i>}
			<label>Verbindung</label>
			{depotLocked
				?
					<select value={connectionId || ''} disabled={true}>
						<option>{props.accountLabel || 'aktuelle Verbindung'}</option>
					</select>
				:
					<select value={connectionId || ''} onChange={(e) => { setSelConnectionId(parseInt(e.target.value, 10)); setDepots(null); }}>
						<option value="">{allConnections === null ? 'Lade…' : 'Bitte wählen'}</option>
						{(allConnections || []).map((c) => (
							<option key={c.connectionid} value={c.connectionid}>{c.bankname} – {c.accountlabel}</option>
						))}
					</select>
			}
			<label>Depot</label>
			{depotLocked
				?
					<select value={order.depotId || ''} disabled={true}>
						<option>{props.depotLabel ? 'Depot ' + props.depotLabel : 'aktuelles Depot'}</option>
					</select>
				:
					<select value={order.depotId || ''} onChange={(e) => selectDepot(e.target.value)} disabled={connectionId === null}>
						<option value="">{depots === null ? 'Lade…' : 'Bitte wählen'}</option>
						{(depots || []).map((depot) => (
							<option key={depot.depotId} value={depot.depotId}>Depot {depot.depotDisplayId}</option>
						))}
					</select>
			}
			<label>Wertpapier</label>
			{props.instrumentLabel
				?
					<p className="brokerage-instrument-label">{props.instrumentLabel}{props.instrumentIsin ? ` (${props.instrumentIsin})` : ''}</p>
				:
					<input value={order.instrumentId} onChange={(e) => setOrder({ ...order, instrumentId: e.target.value })} placeholder="ISIN/instrumentId" />
			}
			<label>Handelsplatz</label>
			<select value={order.venueId} onChange={(e) => selectVenue(e.target.value)}>
				<option value="">{venues === null ? 'Lade…' : 'Bitte wählen'}</option>
				{sortedVenues.map((venue) => (
					<option key={venue.venueId} value={venue.venueId}>{venueLabel(venue)}</option>
				))}
			</select>
			{order.side === 'SELL' && order.venueId !== '' && props.custodyCountry && selectedVenue && selectedVenue.country !== props.custodyCountry &&
				<p className="brokerage-hint brokerage-warning">
					<i className="bi bi-exclamation-triangle"></i> Diese Position liegt vermutlich bei einer Lagerstelle in {props.custodyCountry} - ein Verkauf an einem anderen Handelsplatz kann mit "nicht genügend Bestand" fehlschlagen.
				</p>
			}
			{order.venueId !== '' &&
				<React.Fragment>
					<label>Gültig bis</label>
					<select
						value={order.validityType || ''}
						onChange={(e) => setOrder({ ...order, validityType: e.target.value, validity: e.target.value === 'GTD' ? maxValidityDate() : '' })}
					>
						<option value="">Bitte wählen</option>
						{validityOptions.map((type) => (
							<option key={type} value={type}>{type === dayValidity ? 'Tagesgültig' : 'GTC'}</option>
						))}
					</select>
				</React.Fragment>
			}
			<label>Orderart</label>
			<select value={order.side} onChange={(e) => setOrder({ ...order, side: e.target.value })}>
				<option value="BUY">Kauf</option>
				<option value="SELL">Verkauf</option>
			</select>
			<label>Stückzahl</label>
			<input
				type="number"
				min="1"
				step="1"
				value={order.quantity}
				// Nur ganze Stueckzahlen - Nachkommastellen ergeben beim Aktienkauf
				// keinen Sinn (Nutzerwunsch).
				onChange={(e) => setOrder({ ...order, quantity: e.target.value.replace(/[^0-9]/g, '') })}
				onKeyDown={(e) => { if (e.key === 'Enter' && !busy && orderValid()) submitPreview(); }}
				autoFocus={props.initialOrder.side === 'BUY'}
			/>
			<label>Limit ({order.limitCurrency || 'EUR'})</label>
			<input
				type="number"
				min="0"
				step="0.01"
				value={order.limitPrice}
				onChange={(e) => setOrder({ ...order, limitPrice: e.target.value })}
				onKeyDown={(e) => { if (e.key === 'Enter' && !busy && orderValid()) submitPreview(); }}
				autoFocus={props.initialOrder.side === 'SELL'}
			/>
			{error !== '' && <p className="brokerage-error"><i className="bi bi-exclamation-triangle"></i> {error}</p>}
			<div className="brokerage-actions">
				<button type="button" className="btn" disabled={busy || !orderValid()} onClick={submitPreview}>
					{busy ? 'Sende…' : 'Order absenden'}
				</button>
			</div>
		</div>
	)

};
